import { useState, useCallback, useRef } from "react";
import { Loader2, X, FileMusic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { api } from '@/services/authService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SelectedFile {
  file: File;
  id: string;
  status: 'checking' | 'valid' | 'invalid' | 'duplicate' | 'uploading' | 'uploaded' | 'error';
  errorMessage?: string;
  progress?: number;
  lastLoaded?: number;
}

const SongUploader = ({ onUploadComplete }: { onUploadComplete: () => void }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(0);
  const [storageType, setStorageType] = useState<'local' | 'cdn' | 'cloudflare'>('cloudflare');
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | File[]) => {
    const newFiles = Array.from(files).map(file => ({
      file,
      id: Math.random().toString(36).substring(7),
      status: 'checking' as const
    }));

    setSelectedFiles(prev => [...prev, ...newFiles]);

    for (const newFile of newFiles) {
      try {
        if (!newFile.file.type.startsWith('audio/')) {
          setSelectedFiles(prev => prev.map(f => 
            f.id === newFile.id 
              ? { ...f, status: 'invalid', errorMessage: 'Desteklenmeyen format' }
              : f
          ));
          continue;
        }

        const hasSameNameInSelected = selectedFiles.some(f => 
          f.id !== newFile.id &&
          f.status === 'valid' &&
          f.file.name.toLowerCase() === newFile.file.name.toLowerCase()
        );

        if (hasSameNameInSelected) {
          setSelectedFiles(prev => prev.map(f => 
            f.id === newFile.id 
              ? { ...f, status: 'duplicate', errorMessage: 'Bu şarkı zaten seçili' }
              : f
          ));
          continue;
        }

        const response = await api.get(`/songs/check`, {
          params: {
            filename: newFile.file.name
          }
        });

        const { exists } = response.data;
        
        setSelectedFiles(prev => prev.map(f => 
          f.id === newFile.id 
            ? { 
                ...f, 
                status: exists ? 'duplicate' : 'valid',
                errorMessage: exists ? 'Bu şarkı zaten yüklenmiş' : undefined
              }
            : f
        ));

      } catch (error) {
        console.error('Dosya kontrolü sırasında hata:', error);
        setSelectedFiles(prev => prev.map(f => 
          f.id === newFile.id 
            ? { 
                ...f, 
                status: 'invalid', 
                errorMessage: error instanceof Error ? error.message : 'Kontrol sırasında bilinmeyen bir hata oluştu'
              }
            : f
        ));
      }
    }

    const stats = newFiles.reduce((acc, file) => {
      const status = selectedFiles.find(f => f.id === file.id)?.status;
      if (status) acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    if (stats.duplicate || stats.invalid) {
      toast({
        title: "Bazı dosyalar eklenemedi",
        description: [
          stats.valid && `${stats.valid} şarkı başarıyla eklendi.`,
          stats.duplicate && `${stats.duplicate} şarkı zaten mevcut.`,
          stats.invalid && `${stats.invalid} şarkı eklenemedi.`
        ].filter(Boolean).join(' ')
      });
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFiles(files);
    }
  }, [handleFiles]);

  const removeFile = (id: string) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== id));
  };

  const handleUpload = async () => {
    const validFiles = selectedFiles.filter(file => file.status === 'valid' || file.status === 'duplicate');
    if (validFiles.length === 0) {
      toast({
        title: "Hata",
        description: "Yüklenecek geçerli dosya yok",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    setProgress(0);
    setUploadedFiles(0);
    
    // Tüm dosyaları uploading durumuna getir
    setSelectedFiles(prev => prev.map(file => 
      file.status === 'valid' || file.status === 'duplicate'
        ? { ...file, status: 'uploading', progress: 0 }
        : file
    ));

    let uploadedCount = 0;
    const totalFiles = validFiles.length;
    
    // Maksimum 8 dosya aynı anda yüklenecek şekilde paralelleştirme
    const BATCH_SIZE = 8;
    
    const uploadFile = async (selectedFile: SelectedFile) => {
      try {
        console.time(`upload-${selectedFile.id}`);
        console.log(`Yükleme başlıyor: ${selectedFile.file.name} (${formatFileSize(selectedFile.file.size)})`);
        
        const formData = new FormData();
        formData.append('file', selectedFile.file);
        formData.append('storageType', storageType);
        
        // Gerekli alanları ekle
        formData.append('createdBy', 'admin'); // Kullanıcı kimliğini ekleyin, örneğin admin veya gerçek bir kullanıcı ID'si
        
        // Status değerini "active" olarak ayarla (model yalnızca 'active' veya 'inactive' kabul ediyor)
        formData.append('status', 'active');

        // Dosya durumunu güncelle
        setSelectedFiles(prev => prev.map(f => 
          f.id === selectedFile.id ? { ...f, status: 'uploading', progress: 0 } : f
        ));

        const startTime = Date.now();
        
        await api.post('/songs/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            const currentTime = Date.now();
            const elapsed = currentTime - startTime;
            const speed = progressEvent.loaded / (elapsed / 1000); // bytes per second
            
            console.log(`Yükleme ilerlemesi: ${selectedFile.file.name} - %${percentCompleted} - Hız: ${formatFileSize(speed)}/s`);
            
            // Bu dosyanın progress değerini güncelle
            setSelectedFiles(prev => prev.map(f => 
              f.id === selectedFile.id ? { 
                ...f, 
                progress: percentCompleted,
                lastLoaded: progressEvent.loaded 
              } : f
            ));
          }
        });

        console.timeEnd(`upload-${selectedFile.id}`);
        console.log(`Yükleme tamamlandı: ${selectedFile.file.name}`);

        // Başarıyla yüklenen dosyayı işaretle
        setSelectedFiles(prev => prev.map(f => 
          f.id === selectedFile.id ? { ...f, status: 'uploaded', progress: 100 } : f
        ));
        
        // Yüklenen dosya sayısını güncelle
        uploadedCount++;
        setUploadedFiles(uploadedCount);
        
        // Genel ilerleme çubuğunu güncelle - en basit ve güvenilir yöntem
        const overallProgress = (uploadedCount / totalFiles) * 100;
        setProgress(overallProgress);
        
        console.log(`Dosya başarıyla yüklendi: ${selectedFile.file.name}`);
        return true;
      } catch (error) {
        console.error('Yükleme hatası:', error);
        // Hatayı dosya durumunda göster
        setSelectedFiles(prev => prev.map(f => 
          f.id === selectedFile.id ? { ...f, status: 'error', errorMessage: 'Yükleme başarısız' } : f
        ));
        return false;
      }
    };

    // Dosyaları daha küçük gruplara böl ve paralel yükle
    for (let i = 0; i < validFiles.length; i += BATCH_SIZE) {
      const batch = validFiles.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(file => uploadFile(file)));
    }

    setUploading(false);
    
    // API çağrısından sonra sayfa verilerini güncelle
    if (uploadedCount > 0) {
      toast({
        title: "Başarılı",
        description: `${uploadedCount} şarkı başarıyla yüklendi.`,
        variant: "default",
        className: "bg-green-50 border-green-200",
      });
      onUploadComplete();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    handleFiles(files);
    event.target.value = '';
  };

  const handleClearSelectedFiles = () => {
    setSelectedFiles([]);
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Select
          value={storageType}
          onValueChange={(value: 'local' | 'cdn' | 'cloudflare') => setStorageType(value)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Depolama türünü seçin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="local">Lokal Depolama</SelectItem>
            <SelectItem value="cdn">CDN</SelectItem>
            <SelectItem value="cloudflare">Cloudflare R2</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div 
        className={cn(
          "border-2 border-dashed rounded-lg p-6 transition-colors text-center",
          isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/20",
          uploading ? "pointer-events-none" : "cursor-pointer hover:border-primary hover:bg-primary/5"
        )}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleUploadClick}
      >
        <input 
          id="fileInput"
          type="file" 
          multiple 
          accept="audio/*"
          className="hidden" 
          onChange={handleFileInputChange}
          ref={fileInputRef}
        />
        
        <div className="flex flex-col items-center justify-center gap-2">
          <FileMusic className="h-10 w-10 text-muted-foreground" />
          <h3 className="text-lg font-medium">
            Dosyaları buraya sürükleyin veya yüklemek için tıklayın
          </h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            MP3, WAV, OGG, FLAC dosyalarını destekler. Her dosya en fazla 100MB olabilir.
          </p>
        </div>
      </div>

      {uploading && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {uploadedFiles} / {selectedFiles.length} şarkı yüklendi
            </span>
            <span className="text-sm text-muted-foreground">{progress.toFixed(0)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}

      {selectedFiles.length > 0 && !uploading && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Seçilen Dosyalar ({selectedFiles.length})</h4>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClearSelectedFiles} 
              className="h-8 text-xs"
              disabled={uploading}
            >
              <X className="h-3 w-3 mr-1" />
              Temizle
            </Button>
          </div>
          <div className="max-h-[250px] overflow-y-auto pr-2 border rounded-md">
            {selectedFiles.map((selectedFile) => (
              <div 
                key={selectedFile.id} 
                className={cn(
                  "flex items-center justify-between p-2 text-sm border-b last:border-b-0",
                  selectedFile.status === 'error' ? "bg-red-50" : (selectedFile.status === 'uploaded' ? "bg-green-50" : "")
                )}
              >
                <div className="flex items-center space-x-2 overflow-hidden">
                  <FileMusic className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <div className="truncate max-w-[250px]">
                    <p className="truncate font-medium">{selectedFile.file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.file.size)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  {selectedFile.status === 'error' && (
                    <div className="text-xs text-red-500 mr-2">{selectedFile.errorMessage}</div>
                  )}
                  
                  {selectedFile.status === 'uploading' && (
                    <div className="w-16 mr-2">
                      <Progress value={selectedFile.progress} className="h-1" />
                    </div>
                  )}
                  
                  {selectedFile.status === 'uploaded' && (
                    <div className="text-xs text-green-500 mr-2">Yüklendi</div>
                  )}
                  
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(selectedFile.id)}
                    className="h-6 w-6 p-0 rounded-full"
                    disabled={selectedFile.status === 'uploading'}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-end mt-4">
            <Button 
              onClick={handleUpload}
              disabled={uploading || selectedFiles.filter(f => f.status === 'valid').length === 0}
              className="flex items-center"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Yükleniyor...
                </>
              ) : (
                <>
                  Yükle ({selectedFiles.filter(f => f.status === 'valid').length} Dosya)
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongUploader;
